// SiteDisclaimer.jsx
import React, { useMemo } from "react";
import "./SiteDisclaimer.css";
import { useLocalContext } from "../../context/LocalContext";

/**
 * Props:
 * - variant: "classic" | "inline" | "bar"
 * - compact: boolean (affects "inline" and "bar")
 *
 * Usage:
 * <SiteDisclaimer variant="classic" />
 * <SiteDisclaimer variant="inline" compact />
 * <SiteDisclaimer variant="bar" compact />
 */
const SiteDisclaimer = ({ variant = "classic", compact = false }) => {
  const { webinfo = {} } = useLocalContext();
  const brand = webinfo?.name || "CloudCore";
  const phone = webinfo?.phone || "";
  const supportEmail = webinfo?.email || "support@example.com";

  const lastUpdated = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  // Slim bar for footer / bottom of page
  if (variant === "bar") {
    return (
      <aside
        className={`disc-bar ${compact ? "disc-bar--compact" : ""}`}
        role="note"
        aria-label="Store and product disclaimer"
      >
        <p>
          This store sells <strong>digital licence codes</strong> for third-party
          software (antivirus, security and related tools). We are an independent
          third-party service provider and are not affiliated with Norton, McAfee
          or any other brand mentioned on this website. We are not the original
          publisher. Features, availability and pricing are set by each vendor
          and may change. Vendor terms apply.{" "}
          <a href="/terms-and-conditions">Terms</a> ·{" "}
          <a href="/privacy-policy">Privacy</a>
        </p>
      </aside>
    );
  }

  // Inline card (good for product / cart pages)
  if (variant === "inline") {
    return (
      <section
        className={`disc-inline ${compact ? "disc-inline--compact" : ""}`}
        aria-labelledby="disc-inline-heading"
      >
        <h2 id="disc-inline-heading">Before you buy a licence</h2>
        <ul className="disc-inline-list">
          <li>
            <strong>{brand}</strong> is an independent third-party service provider
            and online store offering genuine licences for third-party software.
            Unless a product page clearly says otherwise, we are not the software
            manufacturer or their official sales partner.
          </li>
          <li>
            Brand names, logos and screenshots belong to their respective owners and
            are shown only to help you identify the software. Their use here does not
            mean sponsorship, endorsement or affiliation.
          </li>
          <li>
            Product features, device/OS support and regional availability are controlled
            by each vendor and may change over time. Always check the current plan
            details, system requirements and in-app notices during setup.
          </li>
          <li>
            We provide order and licence assistance. Ongoing technical issues inside the
            app (bugs, performance, feature behaviour) are handled under the vendor’s own
            support and licence terms.
          </li>
          <li>
            Orders are delivered as <strong>digital items only</strong> (no boxes, CDs or
            physical shipping). We process only the data needed to complete your order and
            provide service, in line with our{" "}
            <a href="/privacy-policy">Privacy Policy</a>. See{" "}
            <a href="/privacy-policy#your-choices">Your Choices</a> for consent and
            opt-out options where available.
          </li>
        </ul>
        <p className="disc-inline-meta">
          Questions about how this store works? Email{" "}
          <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
          {phone ? (
            <>
              {" "}
              or call{" "}
              <a href={`tel:${webinfo?.phonecall || phone}`}>{phone}</a>
            </>
          ) : null}
          . Last updated: {lastUpdated}.
        </p>

        {/* Optional footer-style 1-liner (inline pages too) */}
        <p className="disc-inline-meta" style={{ marginTop: "0.6rem" }}>
          We are an independent third-party service provider and are not affiliated
          with Norton, McAfee or any other brand mentioned on this website.
        </p>
      </section>
    );
  }

  // Classic full-page disclaimer
  return (
    <section className="disc-classic" aria-labelledby="disc-classic-heading">
      <header className="disc-classic-head">
        <h1 id="disc-classic-heading">Website &amp; Store Disclaimer</h1>
        <p className="disc-classic-sub">
          This page explains how {brand} relates to the brands listed on this website and
          what you should know before purchasing a licence through our store.
        </p>
      </header>

      <div className="disc-classic-body">
        <section>
          <h2>1. Role of this store</h2>

          {/* ✅ UPDATED LINE */}
          <p>
            <strong>{brand}</strong> operates as an{" "}
            <strong>independent third-party service provider and online store</strong>{" "}
            offering antivirus, VPN and other cybersecurity software licences. Unless a
            specific product page clearly states otherwise, we are not the software
            manufacturer, official partner or authorised representative of the brands
            shown on this website.
          </p>

          <p>
            Information on this site is provided for general guidance about available
            plans and options. It is not professional security, legal or financial
            advice. You should review the vendor’s official documentation before making
            important decisions.
          </p>
        </section>

        <section>
          <h2>2. Trademarks, logos &amp; images</h2>
          <p>
            Product names, logos, brand wording and screenshots remain the property of
            their respective owners. They are used solely to describe and identify the
            software being offered (for example, which product a licence activates).
          </p>
          <p>
            Such references do <strong>not</strong> imply that those brands sponsor,
            endorse or are formally affiliated with <strong>{brand}</strong>, unless we
            explicitly state a specific partnership.
          </p>
        </section>

        <section>
          <h2>3. Product details, features &amp; availability</h2>
          <p>
            We make reasonable efforts to keep plan information, feature lists and pricing
            clear and current. However, software vendors may update features, rename
            editions, change limits, alter device/OS support or adjust regional
            availability and pricing without prior notice to us.
          </p>
          <p>
            Before completing a purchase, please review the latest details on the product
            page, any notices shown during activation, and the vendor’s own terms and
            support documentation to confirm that the plan still matches your needs.
          </p>
        </section>

        <section>
          <h2>4. Licences, support &amp; warranties</h2>
          <p>
            When you buy through this website, you are purchasing{" "}
            <strong>a licence to use third-party software</strong> under that vendor’s
            end-user licence agreement (EULA) and policies. Any warranties, service
            commitments, feature roadmaps or technical support for the software are
            provided by the vendor, not by <strong>{brand}</strong>, unless a page
            clearly offers additional support from us.
          </p>
          <p>
            If you experience in-app issues (errors, crashes, detection behaviour, threat
            events), you may need to contact the vendor’s official support channels as
            described in their documentation. We are happy to help you understand which
            support route to use.
          </p>

          {/* ✅ NEW LINE (as requested, at the end of Section 4) */}
          <p>
            We may offer paid assistance for setup, installation, activation and general
            troubleshooting, where clearly stated on a product or support page.
          </p>
        </section>

        <section>
          <h2>5. Orders, data &amp; store policies</h2>
          <p>
            We collect and process only the information needed to complete your order,
            handle billing and provide customer service, as outlined in our{" "}
            <a href="/privacy-policy">Privacy Policy</a>. We do not sell your personal
            data.
          </p>
          <p>
            Your purchases via this website are governed by our{" "}
            <a href="/terms-and-conditions">Terms &amp; Conditions</a> and any other
            applicable policies, including our{" "}
            <a href="/refund-policy">Refund Policy</a> and{" "}
            <a href="/shipping-policy">Delivery Information</a> (covering digital
            delivery). Please review these documents before completing a purchase.
          </p>
        </section>

        <p className="disc-classic-meta">
          Store contact:{" "}
          <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
          {phone ? (
            <>
              {" · "}Phone:{" "}
              <a href={`tel:${webinfo?.phonecall || phone}`}>{phone}</a>
            </>
          ) : null}
          {" · "}Last updated: {lastUpdated}
        </p>

        {/* ✅ FOOTER 1-LINER (you said: every page footer; keep here too) */}
        <p className="disc-classic-meta" style={{ marginTop: "0.6rem" }}>
          We are an independent third-party service provider and are not affiliated with
          Norton, McAfee or any other brand mentioned on this website.
        </p>
      </div>
    </section>
  );
};

export default SiteDisclaimer;
